import { useCallback, useEffect, useMemo, useRef } from 'react';
import { gaPush } from '../../configuration/setup/googleAnalytics';
import useTabActive from '../../hooks/useTabActive';
import { useIsChatInView } from '../chat/chatSlice';

type VideoProps = {
    src: string;
    title?: string;
    gaEventCategory: string;
    startSeconds?: number;
};

type YouTubePlayerInstance = {
    destroy?: () => void;
    pauseVideo?: () => void;
};

type YouTubePlayerEvent = {
    data: number;
};

type YouTubeApi = {
    Player: new (
        elementId: string,
        config: {
            events?: {
                onStateChange?: (event: YouTubePlayerEvent) => void;
            };
        }
    ) => YouTubePlayerInstance;
    PlayerState: {
        PLAYING: number;
    };
};

declare global {
    interface Window {
        YT?: YouTubeApi;
        onYouTubeIframeAPIReady?: () => void;
    }
}

const YOUTUBE_IFRAME_API_URL = 'https://www.youtube.com/iframe_api';
const DEFAULT_YOUTUBE_START_SECONDS = 0;
const apiReadyCallbacks: Array<() => void> = [];
let apiScriptRequested = false;

const loadYouTubeIframeApi = (onReady: () => void) => {
    if (window.YT?.Player) {
        onReady();
        return;
    }

    apiReadyCallbacks.push(onReady);

    if (apiScriptRequested) {
        return;
    }

    const previousOnReady = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
        previousOnReady?.();
        const callbacks = [...apiReadyCallbacks];
        apiReadyCallbacks.length = 0;

        callbacks.forEach(callback => {
            callback();
        });
    };

    apiScriptRequested = true;

    const scriptElement = document.createElement('script');
    scriptElement.src = YOUTUBE_IFRAME_API_URL;
    scriptElement.async = true;

    document.head.appendChild(scriptElement);
};

const getYoutubeEmbedSrc = (source: string, startSeconds: number) => {
    try {
        const url = new URL(source);

        url.searchParams.set('enablejsapi', '1');
        url.searchParams.set('start', String(startSeconds));

        if (window.location.origin) {
            url.searchParams.set('origin', window.location.origin);
        }

        return url.toString();
    } catch {
        return source;
    }
};

export const YouTubeEmbed = ({
    src,
    title = 'YouTube video player',
    gaEventCategory,
    startSeconds = DEFAULT_YOUTUBE_START_SECONDS,
}: VideoProps) => {
    const isBrowserTabActive = useTabActive();
    const isChatInView = useIsChatInView();
    const hasTrackedPlay = useRef(false);
    const playerRef = useRef<YouTubePlayerInstance | undefined>(undefined);
    const shouldPausePlaybackRef = useRef(false);
    const iframeId = useMemo(() => `upselling-youtube-${Math.random().toString(36).slice(2, 11)}`, []);
    const youtubeSrc = useMemo(() => getYoutubeEmbedSrc(src, startSeconds), [src, startSeconds]);

    const pauseVideo = useCallback(() => {
        playerRef.current?.pauseVideo?.();
    }, []);

    const sendGAEvent = useCallback(() => {
        if (hasTrackedPlay.current) {
            return;
        }

        hasTrackedPlay.current = true;

        gaPush({
            category: gaEventCategory,
            action: 'Play video',
            label: 'Embedded YouTube video played by user',
        });
    }, [gaEventCategory]);

    useEffect(() => {
        shouldPausePlaybackRef.current = !isBrowserTabActive || !isChatInView;

        if (shouldPausePlaybackRef.current) {
            pauseVideo();
        }
    }, [isBrowserTabActive, isChatInView, pauseVideo]);

    useEffect(() => {
        let isUnmounted = false;

        loadYouTubeIframeApi(() => {
            if (isUnmounted || !window.YT?.Player) {
                return;
            }

            playerRef.current = new window.YT.Player(iframeId, {
                events: {
                    onStateChange: event => {
                        if (event.data === window.YT?.PlayerState.PLAYING) {
                            sendGAEvent();
                        }
                    },
                },
            });

            if (shouldPausePlaybackRef.current) {
                pauseVideo();
            }
        });

        return () => {
            isUnmounted = true;
            playerRef.current?.destroy?.();
            playerRef.current = undefined;
        };
    }, [iframeId, pauseVideo, sendGAEvent]);

    return (
        <div className='width-100pct'>
            <div className='position-relative width-100pct overflow-hidden rounded' style={{ paddingTop: '56.25%' }}>
                <iframe
                    id={iframeId}
                    src={youtubeSrc}
                    title={title}
                    allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share'
                    referrerPolicy='strict-origin-when-cross-origin'
                    allowFullScreen
                    className='position-absolute inset-0 border-0'
                    style={{ width: '100%', height: '100%' }}
                />
            </div>
        </div>
    );
};
